import React from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Clock, Armchair, ArrowRight } from 'lucide-react';
import './VenueRestaurantFeatured.css';

export interface VenueRestaurantFeaturedData {
  id: string;
  name: string;
  slug: string;
  coverImageUrl: string | null;
  cuisine: string | null;
  subscriptionTier: 'FREE' | 'PRO' | 'PREMIUM';
  dineInSupported: boolean;
  tableCount: number;
  openNow: boolean;
  opensAt: string | null;
  rating: number | null;
  avgPrepTime: string | null;
  tagline: string | null;
}

interface Props {
  restaurant: VenueRestaurantFeaturedData;
  onClick: () => void;
  style?: React.CSSProperties;
}

/**
 * Featured restaurant card — Direction A.
 *  - 16:10 cover-dominated photo
 *  - PREMIUM/featured pill top-left
 *  - open/closed pill top-right
 *  - Fraunces italic name overlaid bottom-left, rating chip bottom-right
 *  - Tagline + cuisine + meta (prepTime, tableCount) + arrow CTA below
 */
function VenueRestaurantFeatured({ restaurant: r, onClick, style }: Props) {
  const { t } = useTranslation();
  const cuisineKey = r.cuisine ?? '';
  const cuisineLabel = cuisineKey
    ? t(`cuisine.${cuisineKey}`, { defaultValue: cuisineKey })
    : '';
  const statusText = r.openNow
    ? t('venueLanding.open')
    : r.opensAt
      ? t('venueLanding.opensAt', { time: r.opensAt })
      : t('venueLanding.closed');
  const ariaLabel = t('venueLanding.cardAriaLabel', {
    name: r.name,
    cuisine: cuisineLabel,
    status: statusText,
  });

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={`vl-feat ${r.openNow ? '' : 'vl-feat--closed'}`}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      aria-label={ariaLabel}
      data-slug={r.slug}
      data-id={r.id}
      style={style}
    >
      <div className="vl-feat-cover">
        {r.coverImageUrl ? (
          <img src={r.coverImageUrl} alt="" />
        ) : (
          <div className="vl-feat-cover-fallback" aria-hidden="true">
            {r.name.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="vl-feat-cover-grad" aria-hidden="true" />

        <div className="vl-feat-tier">
          <span className="vl-feat-tier-dot" aria-hidden="true" />
          {t('venueLanding.featuredBadge', { defaultValue: 'FEATURED' })}
          {r.subscriptionTier === 'PREMIUM' ? ' · PREMIUM' : ''}
        </div>

        <div
          className={`vl-feat-status ${r.openNow ? 'vl-feat-status--open' : 'vl-feat-status--closed'}`}
        >
          <span className="vl-feat-status-dot" aria-hidden="true" />
          {r.openNow
            ? t('venueLanding.open').toUpperCase()
            : t('venueLanding.closed').toUpperCase()}
        </div>

        <div className="vl-feat-cover-txt">
          <h3 className="vl-feat-name">{renderName(r.name)}</h3>
          {r.rating != null && (
            <div className="vl-feat-rating" aria-label={`Rating ${r.rating}`}>
              <Star size={11} fill="#FF8C42" stroke="#FF8C42" aria-hidden="true" />
              <span>{r.rating.toFixed(1)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="vl-feat-body">
        <div className="vl-feat-body-text">
          {cuisineLabel && (
            <div className="vl-feat-cuisine">{cuisineLabel.toUpperCase()}</div>
          )}
          {r.tagline && <div className="vl-feat-tagline">{r.tagline}</div>}
          <div className="vl-feat-meta">
            {r.avgPrepTime && (
              <span className="vl-feat-meta-item">
                <Clock size={11} aria-hidden="true" />
                {r.avgPrepTime}
              </span>
            )}
            {r.avgPrepTime && r.dineInSupported && r.tableCount > 0 && (
              <span className="vl-feat-meta-dot" aria-hidden="true" />
            )}
            {r.dineInSupported && r.tableCount > 0 && (
              <span className="vl-feat-meta-item">
                <Armchair size={12} aria-hidden="true" />
                {r.tableCount}{' '}
                {t('venueLanding.tablesShort', { defaultValue: 'столов' })}
              </span>
            )}
          </div>
        </div>
        <div className="vl-feat-cta" aria-hidden="true">
          <ArrowRight size={16} />
        </div>
      </div>
    </div>
  );
}

/** First word italic-bold (upright bold), remaining words italic on next line. */
function renderName(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return <b>{parts[0]}</b>;
  return (
    <>
      <b>{parts[0]}</b>
      <br />
      {parts.slice(1).join(' ')}
    </>
  );
}

export default VenueRestaurantFeatured;
